import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { personalChatList, recentChatList } from "../apis/apiHandler";

export const chatList = createAsyncThunk(
    "chat/chat_list",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await recentChatList(payload);

            if (response?.code === 200 || response?.code === 404) {
                return response.data;
            } else {
                return rejectWithValue(response);
            }
        } catch (err) {
            console.log("err: ", err);
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const messageList = createAsyncThunk(
    "chat/message_list",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await personalChatList(payload);

            if (response?.code === 200 || response?.code === 404) {
                return response.data;
            } else {
                return rejectWithValue(response);
            }
        } catch (err) {
            console.log("err: ", err);
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const sendMessage = createAsyncThunk(
    "chat/send_message",
    async (payload, { rejectWithValue }) => {
        try {
            let response = {};

            const messageResponse = await personalChatList({
                page_token: 0,
                user_id: payload?.user_id,
                user_role: payload?.user_role,
                limit: 1
            });
            if (messageResponse?.code === 200 || messageResponse?.code === 404) {
                response.messageList = messageResponse.data;
            } else {
                return rejectWithValue(messageResponse);
            }

            const chatResponse = await recentChatList({
                page_token: 0,
                limit: 1
            });
            if (chatResponse?.code === 200 || chatResponse?.code === 404) {
                response.chatList = chatResponse.data;
            } else {
                return rejectWithValue(chatResponse);
            }

            return response;
        } catch (err) {
            console.log("err: ", err);
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const receiveMessage = createAsyncThunk(
    "chat/receive_message",
    async (payload, { rejectWithValue, getState }) => {
        try {
            let response = {};

            const state = getState();
            const currentReceiver = state.chat?.currentReceiver;

            if (currentReceiver?.id == payload?.user_id) {
                const messageResponse = await personalChatList({
                    page_token: 0,
                    user_id: payload?.user_id,
                    user_role: payload?.user_role,
                    limit: 1
                });
                if (messageResponse?.code === 200 || messageResponse?.code === 404) {
                    response.messageList = messageResponse.data;
                } else {
                    return rejectWithValue(messageResponse);
                }
            }

            const chatResponse = await recentChatList({
                page_token: 0,
                limit: 1
            });
            if (chatResponse?.code === 200 || chatResponse?.code === 404) {
                response.chatList = chatResponse.data;
            } else {
                return rejectWithValue(chatResponse);
            }

            return response;
        } catch (err) {
            console.log("err: ", err);
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const initialState = {
    loading: false,
    chatList: [],
    messageList: {
        list: [],
    },
    currentReceiver: null,
    error: null,
    success: false,
};

const chatSlice = createSlice({
    name: "chat",
    initialState,

    reducers: {
        resetMessages(state) {
            state.messageList = {
                list: [],
            };
            // state.currentReceiver = null;    
        },
        resetChatList(state){
            state.messageList = {
                list: [],
            };
            state.chatList = [];
            state.currentReceiver = null; 
        },
        setCurrentReceiver(state, action) {
            state.currentReceiver = action.payload;
        },
        readMessage: (state, action) => {
            const { chat_id, user_id } = action.payload;

            if (state.chatList?.result) {
                state.chatList.result = state.chatList.result.map(chat => {
                    if (chat.chat_id === chat_id && chat.user_id === user_id) {
                        return { ...chat, unreadCount: 0 };
                    }
                    return chat;
                });
            }
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(chatList.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(chatList.fulfilled, (state, { payload }) => {
                state.loading = false;

                if (payload.page_token) {
                    const updatedResult = [...(state.chatList.result || [])];

                    (payload.result || []).forEach((newChat) => {
                        const existingChatIndex = updatedResult.findIndex(chat => chat.id === newChat.id);

                        if (existingChatIndex !== -1) {
                            updatedResult[existingChatIndex] = {
                                ...updatedResult[existingChatIndex],
                                ...newChat,
                            };
                        } else {
                            updatedResult.push(newChat);
                        }
                    });

                    updatedResult.sort((a, b) => new Date(b.update_datetime) - new Date(a.update_datetime));

                    state.chatList = {
                        ...state.chatList,
                        result: updatedResult,
                        page_token: payload.page_token,
                    };
                }
                state.success = true;
            })
            .addCase(chatList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
                state.success = false;
            })

            .addCase(messageList.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(messageList.fulfilled, (state, { payload }) => {
                state.loading = false;

                if (state.messageList?.detail?.chat_id === payload.detail?.chat_id) {
                    state.messageList = {
                        ...payload,
                        list: [...(state.messageList.list || []), ...payload.list],
                    };
                } else {
                    state.messageList = {
                        ...payload,
                        list: payload.list || [],
                    };
                }

                state.success = true;
            })
            .addCase(messageList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
                state.success = false;
            })
            .addCase(sendMessage.fulfilled, (state, { payload }) => {
                state.loading = false;

                state.messageList = {
                    ...state.messageList,
                    list: [...payload?.messageList?.list, ...(state.messageList.list || [])],
                };

                const newChat = payload?.chatList?.result[0];
                if (newChat) {
                    const updatedChatList = [...(state.chatList.result || [])];
                    const existingChatIndex = updatedChatList.findIndex(chat => chat.id === newChat.id);

                    if (existingChatIndex !== -1) {
                        const updatedChat = {
                            ...updatedChatList[existingChatIndex],
                            ...newChat,
                        };
                        updatedChatList.splice(existingChatIndex, 1);
                        state.chatList = {
                            ...state.chatList,
                            result: [updatedChat, ...updatedChatList],
                        };
                    } else {
                        state.chatList = {
                            ...state.chatList,
                            result: [newChat, ...updatedChatList],
                        };
                    }
                }
                state.success = true;
            })
            .addCase(receiveMessage.fulfilled, (state, { payload }) => {
                state.loading = false;

                if (payload?.messageList) {
                    state.messageList = {
                        ...state.messageList,
                        list: [...payload?.messageList?.list, ...(state.messageList.list || [])],
                    };
                }

                const newChat = payload?.chatList?.result[0];
                if (newChat) {
                    const updatedChatList = [...(state.chatList.result || [])];
                    const existingChatIndex = updatedChatList.findIndex(chat => chat.id === newChat.id);

                    if (existingChatIndex !== -1) {
                        const updatedChat = {
                            ...updatedChatList[existingChatIndex],
                            ...newChat,
                        };
                        updatedChatList.splice(existingChatIndex, 1);
                        state.chatList = {
                            ...state.chatList,
                            result: [updatedChat, ...updatedChatList],
                        };
                    } else {
                        state.chatList = {
                            ...state.chatList,
                            result: [newChat, ...updatedChatList],
                        };
                    }
                }
                state.success = true;
            })
    },
});

export const { resetMessages, setCurrentReceiver, readMessage, resetChatList } = chatSlice.actions;
    
export default chatSlice.reducer;